import React from 'react';
import { useShiftStatusQuery } from '@/api/queries/shifts';
import type { ShiftResponse } from '@/api/handlers/resources/shifts/responses';
import {
    forgetSalePointShift,
    getSalePointShift,
    getUser,
    rememberSalePointShift,
    SHIFT_NOT_FOUND,
    type SalePointShift,
} from '@/utils';
import type { ShiftHandover } from './shiftMeta';

export type SalePointHandover = {
    handover: ShiftHandover | null;
    /** `false` while the guard stands; a seller with a shift open closes it instead. */
    canOpenShift: boolean;
    /** Remembers a shift just opened on this device, with its seller. */
    remember: (shift: ShiftResponse) => void;
    /** Forgets the remembered shift when it is the one just closed on this device. */
    forgetIfRemembered: (shift: ShiftResponse) => void;
};

/**
 * The sale point handover guard (SEL-30): cash from two shifts never mixes in
 * one drawer when sellers swap at a device. A shift opened here is remembered
 * with its seller (`src/utils/salePointShift.ts`). When a different seller is
 * signed in, the remembered shift's status is read: while it is open they see
 * whose it is and can't open their own. The memory is forgotten when the status
 * reports the shift closed (by its owner elsewhere, or by an admin), when the
 * server answers `SHIFT_NOT_FOUND`, or when its owner closes it on this device.
 * The owner signing back in is never checked: it is their own drawer.
 *
 * Storage is the memory; `remembered` mirrors it so a change re-renders.
 */
export const useSalePointHandover = (): SalePointHandover => {
    const [remembered, setRemembered] = React.useState<SalePointShift | null>(
        getSalePointShift,
    );

    const userId = getUser()?.id ?? null;
    const guardedShiftId =
        remembered && remembered.sellerId !== userId
            ? remembered.shiftId
            : null;
    const statusQuery = useShiftStatusQuery(guardedShiftId);
    const isCleared =
        statusQuery.data?.status === 'Closed' ||
        statusQuery.error?.code === SHIFT_NOT_FOUND;

    const forget = React.useCallback(() => {
        forgetSalePointShift();
        setRemembered(null);
    }, []);

    React.useEffect(() => {
        if (guardedShiftId !== null && isCleared) {
            forget();
        }
    }, [guardedShiftId, isCleared, forget]);

    const { refetch } = statusQuery;
    const retry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    const handover = React.useMemo<ShiftHandover | null>(() => {
        if (guardedShiftId === null || isCleared) {
            return null;
        }
        // A failed refresh keeps the last answer: `data` is still the open shift.
        if (statusQuery.data?.status === 'Open') {
            return {
                state: 'blocked',
                sellerName: statusQuery.data.sellerName,
                openedAt: statusQuery.data.openedAt,
            };
        }
        return statusQuery.isError && !statusQuery.isFetching
            ? { state: 'unavailable', onRetry: retry }
            : { state: 'checking' };
    }, [
        guardedShiftId,
        isCleared,
        statusQuery.data,
        statusQuery.isError,
        statusQuery.isFetching,
        retry,
    ]);

    const remember = React.useCallback((shift: ShiftResponse) => {
        const next = { shiftId: shift.id, sellerId: shift.openedBySellerId };
        rememberSalePointShift(next);
        setRemembered(next);
    }, []);

    const forgetIfRemembered = React.useCallback(
        (shift: ShiftResponse) => {
            if (getSalePointShift()?.shiftId === shift.id) {
                forget();
            }
        },
        [forget],
    );

    return {
        handover,
        canOpenShift: handover === null,
        remember,
        forgetIfRemembered,
    };
};
